import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { addUnitIdentifiers } from 'stores/packingList/packingList';
import { WebEDIRowData } from 'support/types/webediTable';
import { PackingListResult } from 'components/WebEDI/PackingListModal/types';

type PackingListSnapshot = {
  lineItems: Array<WebEDIRowData>;
  transportUnits: Array<WebEDIRowData>;
};

/**
 * Bridges the packing-list modal with the document form. The modal spans both `line_items`
 * and `transport_units`, which live as sibling top-level arrays on the shared React Hook Form —
 * outside the transport-units DataTableProvider scope — so we reach them via the parent form.
 *
 * The snapshot is taken at open time (not watched) so the modal, which seeds its reducer once
 * on mount, gets stable props for its lifetime. On apply the modal's re-derived flat arrays
 * overwrite both sections wholesale; the debounced re-flatten loop in Datachecks then reconciles
 * uiConfig and validation, so we neither trigger nor validate here.
 *
 * Unit identifiers are a finite resource, so we record every one the builder sees — those present
 * when the modal opens and those in the applied result — in the unitIdentifiers store, letting a
 * new transport unit reuse an identifier no longer in use before minting a fresh one. The store
 * only grows; a deleted unit's identifier stops being in use, so the generator makes it available
 * again on its own (no reconciliation here).
 *
 * We unregister the two sections before writing them. The line-items cells are uncontrolled
 * inputs registered as line_items.<index>.<field>; a split reshapes the array so the input still
 * mounted at a reused index holds a stale DOM ref value, and RHF reads that ref back into
 * _formValues on setValue — reviving a value the serializer intentionally dropped (e.g. a split
 * copy inheriting the previous row's ordered_quantity). Dropping the registrations first removes
 * those refs so only the applied values remain.
 */
export const usePackingListModal = () => {
  const { getValues, setValue, unregister } = useFormContext();
  const [isOpen, setIsOpen] = useState(false);
  const [packingList, setPackingList] = useState<PackingListSnapshot | null>(null);

  const open = useCallback(() => {
    const transportUnits: Array<WebEDIRowData> = getValues('transport_units') ?? [];
    setPackingList({ lineItems: getValues('line_items') ?? [], transportUnits });
    // Seed the reuse set with the identifiers on units present at open, so one freed by a delete
    // this session is reused rather than re-minted.
    addUnitIdentifiers(transportUnits.map((row) => String(row.unit_identifier ?? '')));
    setIsOpen(true);
  }, [getValues]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onApply = useCallback(
    async (result: PackingListResult) => {
      unregister('line_items');
      unregister('transport_units');
      setValue('line_items', result.line_items, { shouldDirty: true });
      setValue('transport_units', result.transport_units, { shouldDirty: true });
      // Record the applied identifiers (any freshly minted ones enter the set for later reuse).
      addUnitIdentifiers(result.transport_units.map((row) => String(row.unit_identifier ?? '')));
      close();
    },
    [unregister, setValue, close],
  );

  return { isOpen, open, close, packingList, onApply };
};
